import { useProposal, CreateProposalDTO } from 'modules/proposals';
import { generateQuoteProposalPageURL } from 'utils/generateQuoteProposalPageURL';
import { useSelector } from 'react-redux';
import { Account } from 'types';
import { collectVariablesForCreateProposal, FinanceProgramFormValues } from 'modules/desking';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { DealBuilderEntity } from 'modules/dealBuilder/api';
import { mapDealBuilderFormValuesToDesking } from '../lib';
import { DealBuilderFormValues } from '../types';
import { Nullable } from 'global';

export const useDealProposalPreview = () => {
  const account = useSelector<any, Account>(state => state.account);
  const {
    isProposalPreviewCreating,
    isQuoteProposalPreviewCreating,
    handleCreatePreviewQuoteProposal,
    handleCreateProposalPreview,
  } = useProposal();

  const handlePreviewQuoteProposal = async (
    activeEntity: DealBuilderEntity,
    equipmentId: Nullable<string>,
    notesToBuyer: Nullable<string>
  ) => {
    const { proposalId: originalProposalId, vendorOpportunityId: voId } = activeEntity.proposal;
    const proposalId = await handleCreatePreviewQuoteProposal({
      voId,
      notesToBuyer,
      proposalSettings: {
        meta: {
          equipmentId: equipmentId,
          originalProposalId: originalProposalId,
        },
      },
    });

    if (!proposalId) return null;

    return generateQuoteProposalPageURL(proposalId, account.dynamicsAccountId!);
  };

  const handlePreviewProposal = async (
    activeEntity: DealBuilderEntity,
    equipmentId: Nullable<string>,
    reviewSendFormValues: DealBuilderFormValues['reviewSend']
  ) => {
    const { sendVia, sendEmailTo, ccEmails, notesToBuyer } = reviewSendFormValues;
    const savedFormData = activeEntity?.proposal?.settings?.configuration?.formData as FinanceProgramFormValues;

    const deskingFormData = mapDealBuilderFormValuesToDesking(
      { sendVia, sendEmailTo, ccEmails, notesToBuyer },
      savedFormData,
      equipmentId!
    );

    const variables = collectVariablesForCreateProposal({
      isDraft: true,
      originalProposalId: activeEntity?.proposal?.proposalId ?? null,
      pricing: activeEntity?.proposal?.settings?.configuration?.pricing,
      formData: deskingFormData,
      menuByTerms: JSON.parse(activeEntity?.proposal?.settings?.configuration?.builder ?? '{}'),
      productsConfigInMenu: JSON.parse(activeEntity?.proposal?.settings?.configuration?.productsConfig ?? '{}'),
      commonProductsConfiguration: JSON.parse(
        activeEntity?.proposal?.settings?.configuration?.commonProductsConfiguration ?? '{}'
      ),
      voId: activeEntity.proposal.vendorOpportunityId,
    });

    const proposalId = await handleCreateProposalPreview({
      ...variables.input,
      notesToBuyer: reviewSendFormValues?.notesToBuyer ?? savedFormData?.notesToBuyer ?? null,
    } as unknown as CreateProposalDTO);

    if (!proposalId) return null;

    return generateProposalPageURL(proposalId, account.dynamicsAccountId!);
  };

  const handlePreviewFromDealSummary = async (activeEntity: DealBuilderEntity) => {
    const equipmentId = activeEntity.quote?.equipment?.[0]?.equipmentId ?? null;
    if (!!activeEntity?.proposal?.settings?.configuration?.pricing) {
      return handlePreviewProposal(activeEntity, equipmentId, {} as DealBuilderFormValues['reviewSend']);
    } else {
      return handlePreviewQuoteProposal(
        activeEntity,
        equipmentId,
        activeEntity?.proposal?.settings?.configuration?.formData?.notesToBuyer ?? null
      );
    }
  };

  return {
    handlePreviewProposal,
    handlePreviewQuoteProposal,
    handlePreviewFromDealSummary,
    isPreviewLoading: isQuoteProposalPreviewCreating || isProposalPreviewCreating,
  };
};
